/**
 * PauseContext
 * ============
 * Tracks the current worker's active pause session across the app.
 *
 * Responsibilities:
 *  - Poll `/api/pause/active` on mount and on window focus
 *  - Expose `startPause()` and `endPause()` helpers
 *  - Keep a live-updated duration counter (seconds) while a session is active
 *  - Listen to WebSocket `pause.started` / `pause.ended` events (best-effort)
 *  - Auto-refresh when a job status changes (to catch auto-end-on-job-start)
 *
 * Scope: intended for roles that can have active jobs (worker, wiegemeister).
 * For other roles the context is inert — components can render conditionally.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PauseContext = createContext({
  activeSession: null,
  elapsedSeconds: 0,
  todaySummary: null,
  startPause: async () => {},
  endPause: async () => {},
  refreshPause: async () => {},
  loading: false,
});

export const PauseProvider = ({ user, children }) => {
  const [activeSession, setActiveSession] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [todaySummary, setTodaySummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const prevSessionIdRef = useRef(null);
  
  const roleEligible = !!(user && ['worker', 'wiegemeister', 'manager', 'admin'].includes(user.role));
  
  const refreshPause = useCallback(async () => {
    if (!roleEligible) return;
    try {
      const [activeRes, todayRes] = await Promise.all([
        axios.get(`${API_URL}/pause/active`),
        axios.get(`${API_URL}/pause/me/today`),
      ]);
      const session = activeRes.data?.active ? activeRes.data.session : null;
      setActiveSession(session);
      setTodaySummary(todayRes.data || null);
    } catch (e) {
      // silent — pause is non-critical
    }
  }, [roleEligible]);
  
  const startPause = useCallback(async (reason, triggered_via = 'header_button', note = null) => {
    if (!roleEligible) return null;
    // Defensiv: KEIN impliziter Default mehr — Aufrufer MUSS reason explizit setzen.
    // Frueher war 'statutory' der Default, was bei einem versehentlichen startPause()
    // ohne Argument eine gesetzliche Pause ausgeloest haette. Nicht mehr.
    if (!reason || !['statutory', 'operational', 'other'].includes(reason)) {
      // eslint-disable-next-line no-console
      console.error('[PauseContext] startPause requires explicit reason ("statutory" | "operational" | "other")');
      toast.error('Pausen-Grund fehlt — Pause wurde nicht gestartet.');
      return null;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/pause/start`, { reason, triggered_via, note });
      setActiveSession(res.data);
      // Sofortiges UI-Update: WorkerDashboard refresht die Job-Liste ohne auf Poll/WS zu warten
      window.dispatchEvent(new Event('pause:changed'));
      const label = reason === 'statutory' ? 'Gesetzliche Pause' : (reason === 'operational' ? 'Pause (Warten)' : 'Pause');
      const affected = (res.data.affected_job_ids || []).length;
      toast.success(`${label} gestartet${affected > 0 ? ` · ${affected} Job${affected > 1 ? 's' : ''} unterbrochen` : ''}`, { duration: 2500 });
      axios.get(`${API_URL}/pause/me/today`).then(r => setTodaySummary(r.data)).catch(() => {});
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Pause konnte nicht gestartet werden';
      toast.error(detail);
      // Do NOT rethrow — callers don't handle rejections.
      return null;
    } finally {
      setLoading(false);
    }
  }, [roleEligible]);
  
  const endPause = useCallback(async (resume_job_id = null) => {
    if (!roleEligible) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/pause/end`, { resume_job_id });
      setActiveSession(null);
      setElapsedSeconds(0);
      window.dispatchEvent(new Event('pause:changed'));
      const s = res.data?.session;
      const duration = s?.duration_minutes ? Math.round(s.duration_minutes) : 0;
      const isStat = s?.reason_type === 'statutory';
      const valid = s?.valid_for_arbzg;
      let msg = `Pause beendet · ${duration} Min`;
      if (isStat && !valid && duration > 0) {
        msg += ' · unter 15 Min, nicht als ArbZG-Pause zaehlt';
      }
      toast.success(msg, { duration: 3500 });
      if ((s?.affected_job_ids || []).length > 0) {
        toast.info('Deine Jobs bleiben unterbrochen — bitte aktiv fortsetzen.', { duration: 4000 });
      }
      axios.get(`${API_URL}/pause/me/today`).then(r => setTodaySummary(r.data)).catch(() => {});
      return res.data;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // Session was already closed (e.g. via auto-end on explicit job start).
        // This is not an error for the user — just sync the UI quietly.
        setActiveSession(null);
        setElapsedSeconds(0);
        axios.get(`${API_URL}/pause/me/today`).then(r => setTodaySummary(r.data)).catch(() => {});
        return { session: null, already_ended: true };
      }
      const detail = err?.response?.data?.detail || 'Pause konnte nicht beendet werden';
      toast.error(detail);
      // Do NOT rethrow — callers in banner/header don't handle rejections.
      return null;
    } finally {
      setLoading(false);
    }
  }, [roleEligible]);
  
  // Initial load + focus refresh
  useEffect(() => {
    if (!roleEligible) return;
    refreshPause();
    const onFocus = () => refreshPause();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [roleEligible, refreshPause]);
  
  // Listen for WebSocket-based pause events (custom event emitted by WebSocketContext)
  useEffect(() => {
    if (!roleEligible) return;
    const onWsPause = () => refreshPause();
    window.addEventListener('ws:pause.started', onWsPause);
    window.addEventListener('ws:pause.ended', onWsPause);
    return () => {
      window.removeEventListener('ws:pause.started', onWsPause);
      window.removeEventListener('ws:pause.ended', onWsPause);
    };
  }, [roleEligible, refreshPause]);
  
  // Live tick every second while session is active
  useEffect(() => {
    if (!activeSession?.started_at) {
      setElapsedSeconds(0);
      prevSessionIdRef.current = null;
      return;
    }
    prevSessionIdRef.current = activeSession.id;
    const start = new Date(activeSession.started_at).getTime();
    const tick = () => setElapsedSeconds(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [activeSession?.id, activeSession?.started_at]);
  
  // Polling fallback: while a session is active, re-check the server every 30s
  // to catch auto-end events that did not reach us via WebSocket (backgrounded tab,
  // dropped connection, etc). Cheap — one GET every half minute.
  useEffect(() => {
    if (!activeSession?.id) return;
    const iv = setInterval(() => { refreshPause(); }, 30000);
    return () => clearInterval(iv);
  }, [activeSession?.id, refreshPause]);
  
  return (
    <PauseContext.Provider value={{ activeSession, elapsedSeconds, todaySummary, startPause, endPause, refreshPause, loading }}>
      {children}
    </PauseContext.Provider>
  );
};

export const usePause = () => useContext(PauseContext);
